import { LOCALES } from "@nh/shared";
import type { FormWizardStep } from "@/shared/ui/core";

/**
 * ═══════════════════════════════════════════════════════════════════
 * Los DETALLES del producto, paso a paso.
 *
 * Es la segunda mitad de un producto: los once bloques de la ficha larga
 * que se leen en su página pública. Se escriben SOBRE un producto que ya
 * existe —pestaña "Detalles"— y no durante el alta, porque escribirlos
 * lleva una tarde y un producto sin ellos es perfectamente válido.
 *
 * ── Por qué también es un asistente ─────────────────────────────────
 * Once bloques en una sola pantalla es justo lo tedioso que se quiso
 * evitar sacándolos del alta: son más de sesenta campos, y aunque estén
 * plegados, quien entra a escribir el cierre pasa por delante de todo.
 * Repartidos en cuatro pasos con progreso, cada pantalla es un trabajo
 * que se empieza y se termina.
 *
 * ── El reparto no es por cantidad: es por lo que se lee ─────────────
 * Los cuatro pasos siguen el orden de la ficha en la tienda, que es
 * también el orden en que se piensa un producto: primero lo que se ve al
 * lado del botón de comprar, luego qué lleva, luego por qué creerlo, y
 * al final la frase con la que se cierra.
 *
 * ── Y se puede guardar desde cualquiera ─────────────────────────────
 * Media ficha es mejor que una plantilla con huecos que gritan "falta
 * contenido", y muchísimo mejor que un producto sin publicar esperando a
 * que alguien tenga una tarde libre. Ningún bloque es obligatorio en el
 * contrato, así que guardar a medias es guardar bien.
 * ═══════════════════════════════════════════════════════════════════
 */
export interface ProductDetailStep extends FormWizardStep {
  /**
   * Las RAÍCES de los bloques que se escriben en el paso, sin el idioma:
   * `"badgeLabel"`, `"pillars"`. El idioma lo pone `fieldsOfDetailStep`,
   * que las expande a `editorial.es.pillars` y `editorial.en.pillars`.
   *
   * Se guardan sin idioma porque el paso enseña los DOS: la pestaña de
   * idioma cambia lo que se pinta, no de qué trata el paso, y un error
   * del servidor en el inglés tiene que devolver a este mismo paso.
   */
  readonly blocks: readonly string[];
}

export const PRODUCT_DETAIL_STEPS: readonly ProductDetailStep[] = [
  {
    key: "cover",
    label: "Portada",
    description: "Lo que se lee junto al botón de comprar: la etiqueta, el precio tachado y los cuatro datos de un vistazo.",
    blocks: ["badgeLabel", "compareAt", "metadata"],
  },
  {
    key: "composition",
    label: "Composición",
    description: "Qué lleva dentro: los pilares, los ingredientes y la tabla nutrimental.",
    blocks: ["pillars", "ingredients", "specTable"],
  },
  {
    key: "backing",
    label: "Respaldo",
    description: "Por qué creerlo: cómo se hace y los números que lo sostienen.",
    blocks: ["process", "stats"],
  },
  {
    key: "closing",
    label: "Cierre",
    description: "Con qué se cierra: la comparación, la nota destacada y la frase final.",
    blocks: ["sideBySide", "callout", "closing"],
  },
];

/**
 * Los campos CONCRETOS de un paso, listos para `form.trigger(...)`.
 *
 * Cada bloque se expande a sus DOS idiomas. Validar solo el que está a la
 * vista dejaría pasar un inglés a medias, y el aviso saltaría al guardar
 * sobre una pestaña que no se está mirando.
 *
 * Un índice fuera de rango devuelve la lista vacía en vez de reventar:
 * "no hay nada que validar" es la respuesta honesta para un paso que no
 * existe.
 */
export function fieldsOfDetailStep(step: number): string[] {
  const blocks = PRODUCT_DETAIL_STEPS[step]?.blocks ?? [];
  return blocks.flatMap((block) =>
    LOCALES.map((locale) => `editorial.${locale}.${block}`),
  );
}

/**
 * A qué paso pertenece un campo que señaló el SERVIDOR.
 *
 * El servidor habla en rutas del contrato
 * (`editorial.es.pillars.items.0.title`) y esas rutas coinciden con los
 * nombres del formulario. Devuelve `-1` cuando el campo no es de ningún
 * paso —un campo del asistente de alta, por ejemplo—: quedarse donde se
 * está es mejor que saltar al paso 1 sin motivo.
 */
export function detailStepOfField(path: string): number {
  return PRODUCT_DETAIL_STEPS.findIndex((step) =>
    step.blocks.some((block) =>
      LOCALES.some((locale) => {
        const root = `editorial.${locale}.${block}`;
        return path === root || path.startsWith(`${root}.`) || path.startsWith(`${root}[`);
      }),
    ),
  );
}
